import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useSurveys from '@hooks/useSurveys';
import { useSurvey } from '@contexts/SurveyContext';
import { useRecording } from '@contexts/RecordingContext';
import { ArrowLeft, Archive, Search, Filter } from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '@config/firebase';
import { Button } from '@common/Button';
import { Input, Select } from '@common/FormFields';
import { logError, logInfo } from '@utils/logger';
import { SurveyList } from '../RoadScopePage/SurveyList'; 
import { useSurveyActions } from '../RoadScopePage/hooks/useSurveyActions';

export const ArchivesPage = () => {
  const navigate = useNavigate();
  const { activeSurvey } = useSurvey();
  const { isRecording } = useRecording();
  const { surveys, loading, error } = useSurveys(true);
  const { handleSurveyEdit, handleLoadSurvey } = useSurveyActions({ 
    setToast: () => {},
    preventRedirect: true, // Add this flag to prevent navigation
    onLoadComplete: () => {
      // Dispatch custom event for local updates
      window.dispatchEvent(new CustomEvent('localStorageUpdate', {
        detail: { 
          key: 'activeSurveyId', 
          newValue: localStorage.getItem('activeSurveyId')
        }
      }));
    }
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    dateRange: 'all',
    status: '',
    customerName: '',
    vehicleType: '',
    dimensions: {
      minLength: '',
      maxLength: '',
      minWidth: '',
      maxWidth: '',
      minHeight: '',
      maxHeight: ''
    },
    route: {
      origin: '',
      destination: ''
    },
    highPole: {
      required: 'all',
      minHeight: '',
      maxHeight: ''
    }
  });
  const [showFilters, setShowFilters] = useState(false);
  // Use the same survey action handlers as the main page
  const handleEdit = handleSurveyEdit;
  const handleLoad = handleLoadSurvey;

  // Filter surveys based on search and filters
  const filteredSurveys = surveys.filter(survey => {
    // Search term filter
    const matchesSearch = !searchTerm || (
      survey.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      survey.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      survey.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Date range filter
    let withinDateRange = true;
    if (filters.dateRange !== 'all') {
      const surveyDate = new Date(survey.updatedAt);
      const now = new Date();
      const dayInMs = 24 * 60 * 60 * 1000;
      switch (filters.dateRange) {
        case '7days':
          withinDateRange = now - surveyDate <= 7 * dayInMs;
          break;
        case '30days':
          withinDateRange = now - surveyDate <= 30 * dayInMs;
          break;
        case '90days':
          withinDateRange = now - surveyDate <= 90 * dayInMs;
          break;
      }
    }

    // Status filter
    const matchesStatus = !filters.status || survey.status === filters.status;

    // Customer filter
    const matchesCustomer = !filters.customerName || 
      survey.customerName?.toLowerCase().includes(filters.customerName.toLowerCase());

    // Vehicle type filter
    const matchesVehicle = !filters.vehicleType || 
      survey.vehicle?.type === filters.vehicleType;

    // Dimensions filter
    const matchesDimensions = (
      (!filters.dimensions.minLength || (survey.vehicle?.length >= Number(filters.dimensions.minLength))) &&
      (!filters.dimensions.maxLength || (survey.vehicle?.length <= Number(filters.dimensions.maxLength))) &&
      (!filters.dimensions.minWidth || (survey.vehicle?.width >= Number(filters.dimensions.minWidth))) &&
      (!filters.dimensions.maxWidth || (survey.vehicle?.width <= Number(filters.dimensions.maxWidth))) &&
      (!filters.dimensions.minHeight || (survey.vehicle?.height >= Number(filters.dimensions.minHeight))) &&
      (!filters.dimensions.maxHeight || (survey.vehicle?.height <= Number(filters.dimensions.maxHeight)))
    );

    // Route info filter
    const matchesRoute = (
      (!filters.route.origin || survey.origin?.toLowerCase().includes(filters.route.origin.toLowerCase())) &&
      (!filters.route.destination || survey.destination?.toLowerCase().includes(filters.route.destination.toLowerCase()))
    );

    // High pole filter
    const matchesHighPole = (
      filters.highPole.required === 'all' ||
      (filters.highPole.required === 'yes' && survey.highPoleHeights?.required) ||
      (filters.highPole.required === 'no' && !survey.highPoleHeights?.required) ||
      (survey.highPoleHeights?.required &&
        (!filters.highPole.minHeight || survey.highPoleHeights.value >= Number(filters.highPole.minHeight)) &&
        (!filters.highPole.maxHeight || survey.highPoleHeights.value <= Number(filters.highPole.maxHeight)))
    );

    return (
      matchesSearch &&
      withinDateRange &&
      matchesStatus &&
      matchesCustomer &&
      matchesVehicle &&
      matchesDimensions &&
      matchesRoute &&
      matchesHighPole
    );
  });

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      {/* Header */}
      <div className="sticky top-0 bg-[#2A2B2E] shadow-sm z-10 p-4 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-white">Archives</h1>
            <p className="text-sm text-gray-400">
              {surveys.length > 0 
                ? `${surveys.length} total ${surveys.length === 1 ? 'survey' : 'surveys'}`
                : 'View all surveys'}
            </p>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mt-4 space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search surveys..."
                icon={Search}
                className="bg-gray-700/30 text-white"
              />
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-700/30 rounded-lg">
              {/* Basic Filters */}
              <Select
                label="Date Range"
                value={filters.dateRange}
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                className="bg-gray-700 text-white border-gray-600"
                labelClassName="text-gray-300"
              >
                <option value="all">All Time</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
              </Select>

              <Select
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="bg-gray-700 text-white border-gray-600"
                labelClassName="text-gray-300"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="in_progress">In Progress</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </Select>

              <Input
                label="Customer Name"
                value={filters.customerName}
                onChange={(e) => setFilters(prev => ({ ...prev, customerName: e.target.value }))}
                className="bg-gray-700 text-white border-gray-600"
                labelClassName="text-gray-300"
                placeholder="Filter by customer..."
              />

              <Select
                label="Vehicle Type"
                value={filters.vehicleType}
                onChange={(e) => setFilters(prev => ({ ...prev, vehicleType: e.target.value }))}
                className="bg-gray-700 text-white border-gray-600"
                labelClassName="text-gray-300"
              >
                <option value="">All Types</option>
                <option value="Step Deck">Step Deck</option>
                <option value="Flatbed">Flatbed</option>
                <option value="RGN">RGN</option>
                <option value="Double Drop">Double Drop</option>
                <option value="Extendable">Extendable</option>
                <option value="Lowboy">Lowboy</option>
              </Select>

              {/* Vehicle Dimensions */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">Vehicle Dimensions</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Min Length"
                    type="number"
                    value={filters.dimensions.minLength}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dimensions: { ...prev.dimensions, minLength: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                  <Input
                    placeholder="Max Length"
                    type="number"
                    value={filters.dimensions.maxLength}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dimensions: { ...prev.dimensions, maxLength: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Min Width"
                    type="number"
                    value={filters.dimensions.minWidth}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dimensions: { ...prev.dimensions, minWidth: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                  <Input
                    placeholder="Max Width"
                    type="number"
                    value={filters.dimensions.maxWidth}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dimensions: { ...prev.dimensions, maxWidth: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Min Height"
                    type="number"
                    value={filters.dimensions.minHeight}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dimensions: { ...prev.dimensions, minHeight: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                  <Input
                    placeholder="Max Height"
                    type="number"
                    value={filters.dimensions.maxHeight}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dimensions: { ...prev.dimensions, maxHeight: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                </div>
              </div>

              {/* Route Info */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">Route Information</label>
                <Input
                  placeholder="Origin"
                  value={filters.route.origin}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    route: { ...prev.route, origin: e.target.value }
                  }))}
                  className="bg-gray-700 text-white border-gray-600"
                />
                <Input
                  placeholder="Destination"
                  value={filters.route.destination}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    route: { ...prev.route, destination: e.target.value }
                  }))}
                  className="bg-gray-700 text-white border-gray-600"
                />
              </div>

              {/* High Pole Requirements */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">High Pole Requirements</label>
                <Select
                  value={filters.highPole.required}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    highPole: { ...prev.highPole, required: e.target.value }
                  }))}
                  className="bg-gray-700 text-white border-gray-600 mb-2"
                >
                  <option value="all">All Surveys</option>
                  <option value="yes">Required</option>
                  <option value="no">Not Required</option>
                </Select>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Min Height"
                    type="number"
                    value={filters.highPole.minHeight}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      highPole: { ...prev.highPole, minHeight: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                  <Input
                    placeholder="Max Height"
                    type="number"
                    value={filters.highPole.maxHeight}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      highPole: { ...prev.highPole, maxHeight: e.target.value }
                    }))}
                    className="bg-gray-700 text-white border-gray-600"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Survey List */}
      <SurveyList 
        surveys={filteredSurveys}
        loading={loading}
        error={error}
        activeSurvey={activeSurvey}
        isRecording={isRecording}
        onEdit={handleEdit}
        onLoad={handleLoad}
      />
    </div>
  );
};